

export const PLATFORM_OPTIONS = ['android', 'ios'];

export const PLATFORM_LABELS = { android: 'Android', ios: 'iOS' };

// The statistic each metric's figure is read from, on both sides of the
// SDK-started vs SDK-never-started comparison.
export const PLATFORM_STATISTIC = {
  android: { cpu: 'p90', total_memory: 'p90' },
  ios: { cpu: 'avg', total_memory: 'avg', fps: 'median' },
};

const STARTUP_KPI = {
  key: 'startup',
  title: 'Time added to app start',
  phrase: 'startup time',
  help: 'startup',
  tab: 'Startup',
  unit: 'ms',
  signed: false,
  axisTitle: 'Startup (ms)',
  // Hidden on a platform until its pipeline reports the startup time.
  optional: true,
  read: (summary) => summary?.sdkInitialize ?? null,
  devices: (summary) => summary?.sdkInitializeDevices || [],
  reports: (metric) => metric.group === 'time' && metric.name === 'sdk_initialize',
};

// The figures on the cards, the takeaway, the trend tabs and the runs
// table, per platform. Every figure is a cost: higher is worse.
export const KPIS = {
  android: [
    {
      key: 'cpu', metric: 'cpu', title: 'Extra CPU used by the SDK', phrase: 'CPU cost', help: 'cpu', tab: 'CPU', unit: 'points', signed: true, axisTitle: 'CPU (pp / %)', raw: { label: 'CPU', unit: 'percent' }, read: (summary) => summary?.cpu ?? null,
    },
    {
      key: 'total_memory', metric: 'total_memory', title: 'Extra memory used by the SDK', phrase: 'memory cost', help: 'memory', tab: 'Memory', unit: 'mb', signed: true, axisTitle: 'Memory (MB)', raw: { label: 'memory', unit: 'mb' }, read: (summary) => summary?.memory ?? null,
    },
    {
      key: 'upload', title: 'Session Replay upload', phrase: 'upload rate', help: 'upload', tab: 'Upload', unit: 'kb_per_min', signed: false, axisTitle: 'Upload (KB/min)', aggregate: 'highest', read: (summary) => summary?.srDataUsageKbPerMin ?? null, devices: (summary) => summary?.srDataUsageDevices || [], reports: (metric) => metric.group === 'data_usage' && metric.unit === 'kb_per_min',
    },
    STARTUP_KPI,
  ],
  ios: [
    {
      key: 'cpu', metric: 'cpu', title: 'Extra CPU used by the SDK', phrase: 'CPU cost', help: 'cpu', tab: 'CPU', unit: 'points', signed: true, axisTitle: 'CPU (pp / %)', raw: { label: 'CPU', unit: 'percent' }, read: (summary) => summary?.cpu ?? null,
    },
    {
      key: 'total_memory', metric: 'total_memory', title: 'Extra memory used by the SDK', phrase: 'memory cost', help: 'memory', tab: 'Memory', unit: 'mb', signed: true, axisTitle: 'Memory (MB)', raw: { label: 'memory', unit: 'mb' }, read: (summary) => summary?.memory ?? null,
    },
    {
      key: 'fps', metric: 'fps', title: 'FPS lost to the SDK', phrase: 'FPS cost', help: 'fps', tab: 'FPS', unit: 'fps', signed: true, axisTitle: 'FPS', raw: { label: 'FPS', unit: 'fps' }, read: (summary) => summary?.fps ?? null,
    },
    STARTUP_KPI,
  ],
};

export const STATISTIC_LABELS = {
  android: { cpu: 'Peak (p90)', total_memory: 'Peak (p90)' },
  ios: { cpu: 'Average', total_memory: 'Average', fps: 'Median' },
};

// Session Replay capture stages, per platform. The iOS SDK only times the
// first two; Android also reports Image Generation. Each stage keeps one
// color in the trend.
const SR_STAGES = [
  { metric: 'sr_perf_01', label: 'UI Parse', help: 'uiparse', color: '#2563eb', platforms: ['android', 'ios'] },
  { metric: 'sr_perf_02', label: 'Regex Masking', help: 'regex', color: '#c2410c', platforms: ['android', 'ios'] },
  { metric: 'sr_perf_03', label: 'Image Generation', help: 'imagegen', color: '#0d9488', platforms: ['android'] },
];

// Android also reports main thread allocations, a count drawn on its own axis.
export const SR_ALLOCATIONS_STAGE = {
  metric: 'sr_main_thread_alloc_count', label: 'Main thread allocations', help: 'alloc', color: '#9333ea', unit: 'count', yAxisID: 'allocations',
};

// Android reports each stage under both scenarios; the Session Replay one is
// the measurement that matters. The iOS summary has no scenario dimension.
export const SR_STAGE_SCENARIO = { android: 'Session Replay' };

export function srStagesFor(platform) {
  return SR_STAGES.filter((stage) => stage.platforms.includes(platform));
}

export const DEVICE_ALL = 'all';

export const SDK_VERSION_ALL = 'all';

export const SDK_RUN_ALL = 'all';

// Raw per-start-mode CPU/memory/FPS from Quality Service.
export const PERFORMANCE_VARIANT_GROUP = 'performance_variant';

export const SDK_ENABLED_START_MODE = 'autostart';

export const SDK_DISABLED_START_MODE = 'manual';

export const NOT_MEASURED_ON_DEVICE = 'Not measured on this device';

export const BASELINE_DAYS = 7;

export const DAY_MS = 24 * 60 * 60 * 1000;

// A usual range needs this many earlier runs to have a meaningful spread.
export const MIN_RANGE_RUNS = 3;

// Below this the displayed figures print the same.
export const DISPLAY_EPSILON = 0.05;

// A release-to-release move counts only when it reaches both this share of
// the previous release's figure and the KPI's noise floor.
export const RELEASE_CHANGE_RATIO = 0.1;

export const RELEASE_NOISE_FLOOR = {
  cpu: 0.5, total_memory: 2, fps: 1, startup: 20, upload: 10,
};
